import type { AppStrings } from './types.js';
import { plural } from './plural.js';

export const en: AppStrings = {
  // COMMON
  error_title: 'Error',
  cancel: 'Cancel',
  retry: 'Retry',
  back_arrow: '← Back',

  // ASK SCREEN
  ask_box_unavailable: 'Box unavailable',
  ask_box_unavailable_desc: 'The link may be outdated or the owner closed their box.',
  ask_create_own_box: 'Create your own box',
  ask_sent_title: 'Sent ✅',
  ask_sent_desc: 'Your anonymous question has been delivered to the box owner.',
  ask_anonymous_hint: 'Your question will be sent anonymously.',
  ask_placeholder: 'Write your question…',
  ask_send: 'Send',
  ask_sending: 'Sending…',
  ask_error_rate_limit: 'Too many requests — please wait a minute.',
  ask_error_restart: 'Please restart the Mini App.',
  ask_error_load: 'Failed to load the box.',
  ask_error_send: 'Failed to send the question.',
  ask_error_banned: 'You are blocked in the app.',
  ask_banned_appeal_btn: 'Appeal',
  ask_appeal_sent: 'Appeal submitted. We will review it manually.',

  // AGE GATE
  age_gate_title: '18+ only',
  age_gate_desc:
    'You are sending an anonymous question. Be respectful: harassment, threats, sexual content involving minors, and any content that violates the law are prohibited. Anonymity does not mean impunity — violations are reported to moderation. By sending a question, you confirm that you are 18+ and accept the rules.',
  age_gate_confirm: "I'm 18+ — continue",

  // INBOX SCREEN
  inbox_title: 'Your box',
  box_link_accordion: 'Invite link',
  inbox_notifications_ask: '🔔 Allow the bot to send notifications about new questions?',
  inbox_notifications_allow: 'Allow',
  inbox_notifications_dismiss_label: 'Dismiss',
  inbox_box_toggle_disable: 'Disable questions',
  inbox_box_toggle_enable: 'Enable questions',
  inbox_box_disabled_warning: 'Questions are disabled — no new questions will be accepted.',
  inbox_share_hint: 'Share the link so friends can ask you anonymous questions.',
  inbox_copy: 'Copy',
  inbox_copied: 'Copied',
  inbox_copy_error: 'Error',
  inbox_no_deeplink: 'Link will appear after deploy.',
  inbox_invite_story: 'Invite via Story',
  inbox_invite_chat: 'Invite via chat',
  inbox_card_rendering: 'Invite card is still rendering.',
  inbox_story_unsupported:
    'Stories are not available in this version of Telegram. Try "via chat".',
  inbox_story_error: 'Failed to open Story.',
  inbox_chat_unsupported:
    'Chat sharing is not available in this version of Telegram. Update your client.',
  inbox_chat_error: 'Failed to share. Please try again.',
  inbox_chat_prepare_error: 'Failed to prepare the invite.',
  inbox_filter_incoming: 'Incoming',
  inbox_filter_hidden: 'Hidden',
  inbox_filter_blocked: 'Blocked',
  inbox_filter_empty: 'Nothing here yet.',
  inbox_acc_unanswered: 'Unanswered',
  inbox_acc_answered: 'Answered',
  inbox_acc_hidden: 'Hidden',
  inbox_acc_blocked: 'Blocked',
  inbox_empty: 'No questions yet. Share the link above.',
  inbox_load_more: 'Load more',
  inbox_load_more_error: 'Failed to load the next page.',
  inbox_error_load: 'Failed to load the box.',
  inbox_rate_limit: 'Too many requests — please wait a minute.',
  inbox_toggle_error: 'Failed to change box status.',

  // MESSAGE BOX (Трек D)
  message_tagline: 'Write to me anonymously',
  message_placeholder: 'Write a message…',
  message_anonymous_hint: 'Your message will be sent anonymously.',
  message_sent_title: 'Sent ✅',
  message_sent_desc: 'Your anonymous message has been delivered.',
  message_owner_entry: 'Write to me',
  message_share_hint: 'Share the link so anyone can send you an anonymous message.',
  message_inbox_open: 'Received messages',
  message_inbox_title: 'Messages',
  message_inbox_empty: 'No messages yet. Share your link above.',

  // ANSWER SCREEN
  answer_title: 'Answer the question',
  answer_placeholder: 'Write your answer…',
  answer_submit: 'Send',
  answer_submitting: 'Sending…',
  answer_back: 'Back',
  answer_error_send: 'Failed to send the answer.',

  // SHARE SCREEN
  share_title: 'Answer published ✅',
  share_hint: 'Share the card and collect even more questions.',
  share_card_rendering_desc:
    'The card is still rendering. Go back to questions and check again shortly — you will be able to share it in a Story or chat.',
  share_card_alt: 'Answer card',
  share_story_btn: 'Share to Story',
  share_story_btn_busy: 'Opening…',
  share_chat_btn: 'Share to chat',
  share_chat_btn_busy: 'Preparing…',
  share_back: 'Back to questions',
  share_story_unsupported:
    'Stories are not available in this version of Telegram. Try "Share to chat".',
  share_story_error: 'Failed to open Story. Try "Share to chat".',
  share_chat_sent: 'Message sent.',
  share_chat_unsupported:
    'Chat sharing is not available in this version of Telegram. Update your client.',
  share_chat_error: 'Failed to share. Please try again.',
  share_chat_prepare_error: 'Failed to prepare the message.',

  // LEADERBOARD SCREEN
  lb_title: 'Leaderboard',
  lb_period_7d: '7 days',
  lb_period_30d: '30 days',
  lb_period_all: 'All time',
  lb_group_label: 'Category',
  lb_group_all: 'All',
  lb_group_ask: 'Ask',
  lb_group_rate: 'Rate',
  lb_group_confession: 'Confession',
  lb_group_guesswho: 'Guess Who',
  lb_group_message: 'Messages',
  lb_error: 'Failed to load leaderboard',
  lb_empty: 'No one in the ranking yet',
  lb_anon: 'Anonymous',
  lb_load_more: 'Load more',
  lb_questions: (n) => `${n} questions`,
  lb_referrals: (n) => `${n} refs`,

  // REWARDS SCREEN
  rw_title: 'My rewards',
  rw_error: 'Failed to load rewards',
  rw_level: (n) => `Level ${n}`,
  rw_confirmed_referrals: (n) => `${n} confirmed referrals`,
  rw_next_level_section: (n) => `NEXT LEVEL — ${n} referrals`,
  rw_to_level: (level, target) => `to level ${level}: ${target}`,
  rw_max_level: 'Maximum level reached!',
  rw_badges_section: 'BADGES',
  rw_formats_section: 'BOX FORMATS',

  // QUESTION CARD
  qcard_status_new: 'New',
  qcard_status_hidden: 'Hidden',
  qcard_status_answered: 'Answered',
  qcard_moderation_flagged: 'Caution: suspicious message',
  qcard_answer_btn: 'Answer',
  qcard_answer_send: 'Send',
  qcard_share_btn: 'Share',
  qcard_hide_btn: 'Hide',
  qcard_show_btn: 'Show',
  qcard_report_btn: 'Report',
  qcard_block_btn: 'Block',
  qcard_unblock_btn: 'Unblock',
  qcard_report_reason_label: 'Select report reason:',
  qcard_reason_spam: 'Spam',
  qcard_reason_harassment: 'Harassment',
  qcard_reason_sexual: 'Sexual content',
  qcard_reason_other: 'Other',
  qcard_error_hide: 'Failed to change visibility.',
  qcard_error_report: 'Failed to send report.',
  qcard_error_block: 'Failed to block the sender.',
  qcard_report_done: 'Report sent — thank you.',
  qcard_block_done: 'Sender blocked.',
  qcard_unblock_done: 'Sender unblocked.',

  // ERRORS (common humanized error messages)
  err_network: 'No connection to server. Check your internet and try again.',
  err_server_unavailable: 'Server is temporarily unavailable, please try again later.',
  err_internal: 'Something went wrong on our end. We are already looking into it — try again later.',
  err_bad_request: 'Please check the data you entered.',
  err_generic: 'Could not complete the action, please try again later.',
  err_schema: 'Data error. Try updating the app.',
  share_story_link_premium_note:
    'A clickable link in Stories requires Telegram Premium. Without it your friends can still find you — your username is shown on the card.',

  // AUTH ERROR SCREEN
  auth_error_title: 'Access error',
  auth_error_desc:
    'Failed to authenticate. Please close and reopen the Mini App from Telegram.',

  // REWARD TOAST
  reward_toast_title: 'New reward!',
  reward_toast_close: '×',

  // BOTTOM TABBAR
  tab_questions: 'Questions',
  tab_leaderboard: 'Leaderboard',
  tab_rewards: 'Rewards',
  tab_settings: 'Settings',

  // SETTINGS SCREEN
  settings_title: 'Settings',
  settings_section_general: 'General',
  settings_notifications: 'New question notifications',
  settings_notifications_hint: 'The bot will message you when someone asks a question.',
  settings_notifications_error: 'Failed to save notification setting.',
  settings_language: 'Language',
  settings_language_error: 'Could not change language. Please try again.',
  settings_section_about: 'About',
  settings_version: 'Version',

  // CODE ENTRY (work-020)
  code_modal_title: 'Story links are Premium-only',
  code_modal_explain:
    'Telegram only gives a clickable story link to Premium subscribers. Instead, add your code to the story — visitors type it in the app and ask you an anonymous question.',
  code_modal_your_code: 'Your code',
  code_modal_copy: 'Copy code',
  code_modal_copied: 'Copied',
  code_modal_share_btn: 'Share to story',
  code_modal_loading: 'Getting your code…',
  code_modal_error: "Couldn't get your code. Try again later.",
  ask_code_title: 'Enter a code',
  ask_code_desc:
    'Came from a story? Find the code in it (e.g. 1234) and type it below.',
  ask_code_placeholder: 'Code from the story',
  ask_code_find: 'Find',
  ask_code_not_found: 'Code not found. Check the digits and try again.',
  ask_code_invalid: 'The code is 4 digits.',
  code_onboarding_header: 'Came from a story?',
  code_onboarding_text: "Enter a friend's code to ask them an anonymous question.",
  code_onboarding_action: 'Enter code',
  settings_ask_by_code: 'Ask by code',
  settings_my_code_title: 'Your story code',
  settings_my_code_hint:
    'Write this code in your story — visitors type it to ask you an anonymous question. Telegram only gives clickable story links to Premium subscribers.',
  settings_my_code_rotate: 'Get a new code',
  settings_my_code_rotating: 'Updating…',
  settings_my_code_done: 'Code updated',
  settings_my_code_error: "Couldn't get your code.",
  settings_my_code_rotate_confirm_title: 'Change your code?',
  settings_my_code_rotate_confirm_desc:
    "Your old code stops working right away. Anyone who saw it in your story won't be able to reach you. Change it only if someone is spamming you.",
  settings_my_code_rotate_confirm_action: 'Change code',

  // BOT
  bot_greeting: (name) => (name ? `Hi, ${name}! 👋` : 'Hi! 👋'),
  bot_onboarding:
    'This is VeylMe — anonymous questions.\n\n' +
    'Open the Mini App, post an “Ask me anonymously” card to your Story — ' +
    'and get anonymous questions right here.\n\n' +
    '⚠️ 18+. Be respectful: bullying, threats and prohibited content are ' +
    'not allowed. Anonymity ≠ impunity — violations go to moderation.',
  bot_open_btn: 'Open VeylMe',
  notify_answer_btn: 'Answer',
  bot_invite_text:
    'Someone shared a VeylMe link.\n\n' +
    'Open the Mini App to ask an anonymous question:',
  bot_invite_btn: 'Ask anonymously',

  // WORK-027 i18n sweep
  tagline: 'Ask me anonymously',
  share_ask_action: 'Ask',
  share_answered_text: 'I answered an anonymous question 👀',
  err_not_in_telegram: 'Mini App is not running in Telegram. Please restart the app.',
  badge_viral_starter: 'Viral start',
  badge_storyteller: 'Storyteller',
  badge_loop_master: 'Loop master',
  theme_confession: 'Confessions',
  theme_guesswho: 'Guess who',
  rw_format_named: (name) => `format «${name}»`,
  card_label_question: 'Question',
  card_label_answer: 'Answer',
  card_invite_subtitle: 'anonymous, no sign-up',
  card_code_hint: 'ENTER THE CODE IN THE APP',
  share_msg_title: (brand) => `${brand} · Anonymous questions`,
  notify_new_question: '🔔 Someone asked you an anonymous question',
  notify_unanswered_reminder: (n) =>
    plural('en', n, { one: '🔔 You have {n} unanswered question', other: '🔔 You have {n} unanswered questions' }),
  notify_ban_permanent: 'permanently',
  notify_ban_until: (date) => `until ${date}`,
  notify_ban_msg: (untilPhrase) =>
    `⛔ You are blocked in VeylMe ${untilPhrase}. Too many recipients reported your messages.\nIf you believe this is a mistake, you can submit an appeal in the app.`,
  notify_warning_msg:
    '⚠️ Several recipients reported your messages. A few more and you will be blocked in VeylMe.',

  // MY QUESTIONS SCREEN + InvitePanel non-premium hint (retention-answer-loop)
  my_questions_title: 'My questions',
  my_questions_empty_title: 'You haven\'t asked anything yet',
  my_questions_empty_desc: 'Ask a question from someone\'s box — the reply will show up here.',
  my_questions_loading: 'Checking for a reply…',
  my_questions_waiting: 'Waiting for a reply',
  my_questions_answer_label: 'Reply',
  settings_my_questions: 'My questions',
  ask_sent_find_reply_hint: 'You can find the reply later in «My questions».',
  inbox_story_nonpremium_hint: 'A Story link isn\'t clickable without Telegram Premium, so we show a code instead — sharing to a chat is more reliable.',

  // SESSION EXPIRY SCREEN (work-038)
  session_expired_title: 'Your Telegram pass has expired',
  session_expired_desc: 'Telegram issues the app a pass once at launch, and this one has expired. Open the app again — we saved the text you started.',
  session_expired_desc_no_storage: 'Your Telegram pass has expired and the app needs to be reopened. We could not save the text you typed — copy it before you leave.',
  session_expired_reopen: 'Open again',
  session_expired_stuck_title: 'Telegram returned the same old pass',
  session_expired_stuck_desc: 'Close the app completely from the bot menu and open it again — then Telegram will issue a new pass.',
  session_auth_failed_title: 'Could not confirm your sign-in',
  session_auth_failed_desc: 'Telegram did not confirm your sign-in. Close the app and open it again from the chat with the bot.',
  session_expired_manual_close: 'The app cannot close itself here — close it yourself and open it again from the chat with the bot.',
  session_expiring_soon: 'Your Telegram pass expires soon. Open the app again so nothing gets lost.',
  my_questions_check_failed: 'Could not check for replies. Please try again later.',
  settings_notifications_load_error: 'Could not load this setting.',
};
